import React, {
  createContext, useCallback, useContext, useMemo,
} from "react"
import { BadgeElement, BadgeElementType } from "sharedTypes/badge"
import moveToEdge from "components/CanvasEditor/lib/order"
import alignObjects, { Alignment } from "components/CanvasEditor/lib/align"
import { ObjectChange } from "components/CanvasEditor/ObjectLayer"
import useElementTransforms, { ElementUpdate } from "../hooks/useElementTransforms"
import useElementOperations from "../hooks/useElementOperations"
import useClipboard from "../hooks/useClipboard"
import { updateTemplateElements } from "../utils/badgeHelpers"
import { useBadgeTemplate } from "../hooks/useBadgeTemplate"
import { useBadgeSelection } from "./BadgeSelectionContext"
import useStableCallbacks from "../hooks/useStableCallbacks"

type BadgeElementState = {
  handleAddElement: (
    elementType: BadgeElementType,
    x: number,
    y: number
  ) => void
  handleElementChange: (
    elementId: string,
    updates: ElementUpdate,
  ) => void
  /** Changes several elements as one step. */
  handleElementsChange: (updates: Map<string, ElementUpdate>) => void
  deleteSelectedElements: () => void
  duplicateSelectedOrById: (elementId?: string) => void
  deleteElementById: (elementId: string) => void
  /** Moves, resizes or rotates several elements as one step. */
  transformElements: (changes: Map<string, ObjectChange>) => void
  /** One step forward / back: the given element, or the first selected one. */
  bringSelectedForward: (elementId?: string) => void
  sendSelectedBackward: (elementId?: string) => void
  /** All the way to the front / back: the given elements, or the selection. */
  bringToFront: (elementIds?: string[]) => void
  sendToBack: (elementIds?: string[]) => void
  /** Lines the selection up; a single element lines up with the badge. */
  alignSelection: (alignment: Alignment) => void
  updateElementZIndex: (elementId: string, zIndex: number) => void
  handleCommitReorder: (newElementsOrder: BadgeElement[]) => void
  handleMoveElement: (direction: "up" | "down" | "left" | "right", distance?: number) => void
  handleCopyElement: () => void
  handlePasteElement: () => void
  handleCutElement: () => void
  hasCopiedElement: boolean
}

const BadgeElementContext = createContext<BadgeElementState | null>(null)

export const useBadgeElement = () => {
  const context = useContext(BadgeElementContext)
  if (!context) {
    throw new Error(
      "useBadgeElement must be used within a BadgeElementProvider",
    )
  }

  return context
}

// Export context for direct use in components that need optional access (e.g., preview mode)
export { BadgeElementContext }

type BadgeElementProviderProps = {
  children: React.ReactNode
}

export const BadgeElementProvider = ({ children }: BadgeElementProviderProps) => {
  // Get dependencies from other contexts
  const { currentTemplate, pushState } = useBadgeTemplate()
  const {
    selectedElementIds,
    selectMultiple,
    setInlineEditingElementId,
  } = useBadgeSelection()

  // Element operations
  const {
    handleAddElement,
    handleDeleteElement: deleteElements,
    handleDuplicateElement: duplicateElements,
  } = useElementOperations(
    currentTemplate,
    pushState,
    selectMultiple,
    setInlineEditingElementId,
  )

  // Element transforms
  const {
    handleElementChange,
    handleElementsChange,
    handleMoveElement,
    handleBringToFront: bringToFront,
    handleSendToBack: sendToBack,
    updateElementZIndex,
    handleCommitReorder,
  } = useElementTransforms(currentTemplate, selectedElementIds, pushState)

  // Clipboard
  const {
    handleCopyElement,
    handlePasteElement,
    handleCutElement,
    hasCopiedElement,
  } = useClipboard(
    currentTemplate,
    selectedElementIds,
    (newElements) => {
      if (!currentTemplate) return
      pushState(updateTemplateElements(
        currentTemplate, (elements) => [...elements, ...newElements],
      ))
      selectMultiple(newElements.map((el) => el.id))
    },
    () => deleteElements(selectedElementIds),
  )

  // Convenience functions that operate on selected elements or accept optional elementId
  const deleteSelectedElements = useCallback(() => {
    deleteElements(selectedElementIds)
  }, [deleteElements, selectedElementIds])

  const duplicateSelectedOrById = useCallback((elementId?: string) => {
    const idsToDuplicate = elementId ? [elementId] : selectedElementIds
    duplicateElements(idsToDuplicate)
  }, [duplicateElements, selectedElementIds])

  const deleteElementById = useCallback((elementId: string) => {
    deleteElements([elementId])
  }, [deleteElements])

  const bringSelectedForward = useCallback((elementId?: string) => {
    const targetElementId = elementId || selectedElementIds[0]
    if (targetElementId) {
      bringToFront(targetElementId)
    }
  }, [bringToFront, selectedElementIds])

  const sendSelectedBackward = useCallback((elementId?: string) => {
    const targetElementId = elementId || selectedElementIds[0]
    if (targetElementId) {
      sendToBack(targetElementId)
    }
  }, [sendToBack, selectedElementIds])

  const moveToEdgeOfStack = useCallback((edge: "front" | "back", elementIds?: string[]) => {
    const ids = elementIds ?? selectedElementIds
    if (!currentTemplate || ids.length === 0) return

    const zIndexes = moveToEdge(currentTemplate.badgeData.elements, ids, edge)
    pushState(updateTemplateElements(currentTemplate, (elements) => elements.map(
      (element) => ({ ...element, zIndex: zIndexes.get(element.id) ?? element.zIndex }),
    )))
  }, [currentTemplate, selectedElementIds, pushState])

  const bringToFrontOfStack = useCallback(
    (elementIds?: string[]) => moveToEdgeOfStack("front", elementIds),
    [moveToEdgeOfStack],
  )
  const sendToBackOfStack = useCallback(
    (elementIds?: string[]) => moveToEdgeOfStack("back", elementIds),
    [moveToEdgeOfStack],
  )

  const transformElements = useCallback((changes: Map<string, ObjectChange>) => {
    if (!currentTemplate || changes.size === 0) return

    pushState(updateTemplateElements(currentTemplate, (elements) => elements.map((element) => {
      const change = changes.get(element.id)
      if (!change) return element
      const { rotation, ...rect } = change

      return {
        ...element,
        ...rect,
        ...(rotation !== undefined && {
          transforms: {
            flipHorizontal: false, flipVertical: false, ...element.transforms, rotate: rotation,
          },
        }),
      }
    })))
  }, [currentTemplate, pushState])

  const alignSelection = useCallback((alignment: Alignment) => {
    if (!currentTemplate || selectedElementIds.length === 0) return

    const selected = currentTemplate.badgeData.elements
      .filter((element) => selectedElementIds.includes(element.id))
      .map((element) => ({ ...element, rotation: element.transforms?.rotate || 0 }))
    const badge = {
      x: 0, y: 0, width: currentTemplate.widthMm, height: currentTemplate.heightMm,
    }
    const positions = alignObjects(selected, alignment, selected.length === 1 ? badge : undefined)
    pushState(updateTemplateElements(currentTemplate, (elements) => elements.map(
      (element) => ({ ...element, ...positions.get(element.id) }),
    )))
  }, [currentTemplate, selectedElementIds, pushState])

  // The same functions on every render, so the elements and panels using them don't re-render
  // each time the template or the selection changes
  const actions = useStableCallbacks({
    handleAddElement,
    handleElementChange,
    handleElementsChange,
    deleteSelectedElements,
    duplicateSelectedOrById,
    deleteElementById,
    transformElements,
    bringSelectedForward,
    sendSelectedBackward,
    bringToFront: bringToFrontOfStack,
    sendToBack: sendToBackOfStack,
    alignSelection,
    updateElementZIndex,
    handleCommitReorder,
    handleMoveElement,
    handleCopyElement,
    handlePasteElement,
    handleCutElement,
  })
  const state = useMemo<BadgeElementState>(
    () => ({ ...actions, hasCopiedElement }),
    [actions, hasCopiedElement],
  )

  return (
    <BadgeElementContext.Provider value={state}>
      {children}
    </BadgeElementContext.Provider>
  )
}
